import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { CLIENT_ACTION } from "../../redux/action";
import { Button } from "../../components";
import { getRolePath, Toaster } from "../../utils/common";
import { useClientCategoryTab } from "../../hooks/useClientCategoryTab";
import "./clientBusinesses.scss";

function personName(person) {
  if (!person) return "—";
  const name = `${person.first_name || ""} ${person.last_name || ""}`.trim();
  return name || person.email || "—";
}

function ClientBusinesses() {
  const { categoryTab } = useClientCategoryTab();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [emailByBusiness, setEmailByBusiness] = useState({});
  const [errorByBusiness, setErrorByBusiness] = useState({});
  const [savingId, setSavingId] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(null);

  const loadBusinesses = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      const response = await CLIENT_ACTION.getMyBusinessAccess();
      if (response?.status === 1 && Array.isArray(response.data)) {
        setBusinesses(response.data);
        return;
      }
      setBusinesses([]);
      setPageError(response?.message || "Could not load your businesses.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (categoryTab !== "business") return;
    loadBusinesses();
  }, [loadBusinesses, categoryTab]);

  if (categoryTab !== "business") {
    return <Navigate to={getRolePath("/dashboard")} replace />;
  }

  const showActionError = (businessId, response) => {
    const text = response?.message || "Something went wrong.";
    setErrorByBusiness((prev) => ({ ...prev, [businessId]: text }));
    if (response?.httpStatus !== 403) Toaster.error(text);
  };

  const handleAdd = async (business) => {
    if (savingId) return;
    const businessId = business.client_business_id;
    const email = String(emailByBusiness[businessId] || "").trim();
    if (!email) {
      setErrorByBusiness((prev) => ({
        ...prev,
        [businessId]: "Enter the email address of the person you want to add.",
      }));
      return;
    }
    setSavingId(businessId);
    setErrorByBusiness((prev) => ({ ...prev, [businessId]: "" }));
    try {
      const response = await CLIENT_ACTION.addBusinessMemberByEmail({
        client_business_id: businessId,
        email,
      });
      if (response?.status === 1) {
        const added = personName(response?.data?.client);
        Toaster.success(
          added && added !== "—"
            ? `${added} now has access to ${business.business_name}.`
            : "Access added.",
        );
        setEmailByBusiness((prev) => ({ ...prev, [businessId]: "" }));
        await loadBusinesses();
        return;
      }
      showActionError(businessId, response);
    } finally {
      setSavingId("");
    }
  };

  const handleRemove = async (business, member) => {
    if (savingId) return;
    const businessId = business.client_business_id;
    const clientId = member?.client_id;
    if (!clientId) return;
    setSavingId(`${businessId}:${clientId}`);
    setErrorByBusiness((prev) => ({ ...prev, [businessId]: "" }));
    try {
      const response = await CLIENT_ACTION.removeBusinessMember({
        client_business_id: businessId,
        client_id: clientId,
      });
      if (response?.status === 1) {
        Toaster.success(
          `${personName(member.client)} no longer has access to ${business.business_name}.`,
        );
        setConfirmRemove(null);
        await loadBusinesses();
        return;
      }
      showActionError(businessId, response);
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="client-businesses-page">
      <div className="client-businesses-intro">
        {/* <h1>Businesses</h1> */}
        <p>
          See who can access each business, add someone by their portal email,
          or remove access.
        </p>
      </div>

      {loading ? (
        <div className="client-businesses-empty">Loading businesses…</div>
      ) : pageError ? (
        <div className="client-businesses-empty">{pageError}</div>
      ) : businesses.length === 0 ? (
        <div className="client-businesses-empty">
          You do not have any businesses yet.
        </div>
      ) : (
        businesses.map((business) => {
          const businessId = business.client_business_id;
          const ownerId = String(
            business.owner?.client_id || business.client_id || "",
          );
          const members = (business.members || []).filter(
            (member) => String(member.client_id) !== ownerId,
          );
          const adding = savingId === businessId;

          return (
            <section key={businessId} className="client-business-card">
              <header className="client-business-card-header">
                <div>
                  <h2>{business.business_name || "Business"}</h2>
                  <p>
                    {[business.business_type, business.business_phone]
                      .filter(Boolean)
                      .join(" · ") || "Business"}
                  </p>
                </div>
                <span
                  className={`client-business-badge${
                    business.can_manage ? " is-owner" : ""
                  }`}
                >
                  {business.can_manage ? "You own this" : "Shared with you"}
                </span>
              </header>

              <div className="client-business-people">
                <h3>People with access</h3>
                <ul>
                  <li>
                    <div>
                      <strong>{personName(business.owner)}</strong>
                      <span>{business.owner?.email || "Owner"}</span>
                    </div>
                    <span className="client-business-role">Owner</span>
                  </li>
                  {members.map((member) => {
                    const removeKey = `${businessId}:${member.client_id}`;
                    const pending = confirmRemove === removeKey;
                    const removing = savingId === removeKey;
                    return (
                      <li key={member.client_business_member_id || member.client_id}>
                        <div>
                          <strong>{personName(member.client)}</strong>
                          <span>{member.client?.email || "—"}</span>
                        </div>
                        {business.can_manage ? (
                          <div className="client-business-remove">
                            {pending ? (
                              <>
                                <button
                                  type="button"
                                  className="client-business-text-btn"
                                  disabled={removing}
                                  onClick={() => setConfirmRemove(null)}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="client-business-text-btn is-danger"
                                  disabled={removing}
                                  onClick={() => handleRemove(business, member)}
                                >
                                  {removing ? "Removing…" : "Confirm"}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="client-business-text-btn is-danger"
                                onClick={() => setConfirmRemove(removeKey)}
                              >
                                Remove access
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="client-business-role">Access</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {members.length === 0 ? (
                  <p className="client-business-hint">
                    No one else has access yet.
                  </p>
                ) : null}
              </div>

              {business.can_manage ? (
                <div className="client-business-add">
                  <label htmlFor={`add-email-${businessId}`}>
                    Add a person by email
                  </label>
                  <div className="client-business-add-row">
                    <input
                      id={`add-email-${businessId}`}
                      type="email"
                      autoComplete="off"
                      placeholder="name@email.com"
                      value={emailByBusiness[businessId] || ""}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleAdd(business);
                        }
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        setEmailByBusiness((prev) => ({
                          ...prev,
                          [businessId]: value,
                        }));
                        if (errorByBusiness[businessId]) {
                          setErrorByBusiness((prev) => ({
                            ...prev,
                            [businessId]: "",
                          }));
                        }
                      }}
                    />
                    <Button
                      text={adding ? "Adding…" : "Add"}
                      disable={adding}
                      handleClick={() => handleAdd(business)}
                    />
                  </div>
                  {errorByBusiness[businessId] ? (
                    <p className="client-business-error" role="alert">
                      {errorByBusiness[businessId]}
                    </p>
                  ) : (
                    <p className="client-business-hint">
                      The email must already belong to a FinloTax account.
                    </p>
                  )}
                </div>
              ) : (
                <p className="client-business-hint">
                  Only the owner can add or remove people.
                </p>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}

export default ClientBusinesses;
